import { HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Params } from '@angular/router';
import { BOOKING_PAGE_VIEWS, CUSTOM_FIELD_TYPES, HIDEABLE_BOOKING_STEPS, SECTION_PRECEDENCE, WIDGET_DISPLAY_TIMEZONE_SOURCE } from '@constants/app.constants';
import { DATE_FORMAT, DATE_ISO_FORMAT, DATE_TIME_ISO_T_FORMAT } from '@constants/date.constants';
import { IS_CALENSO_AUTH_REQUIRED } from '@constants/http-context';
import { BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS } from '@constants/local-storage.constants';
import { AdyenBookingContext } from '@models/adyen.model';
import { AppointmentBookingModel, BookedAppointment } from '@models/appointment-booking.model';
import { AppointmentReservationBody } from '@models/appointment-reservation.model';
import { AppointmentServiceModel } from '@models/appointment-service.model';
import { DayWiseFreeSlotsDao } from '@models/availability.model';
import { CouponDbModel } from '@models/coupon-db.model';
import { CustomFieldModel } from '@models/custom-field.model';
import { CityByZipModel, CustomerDbModel } from '@models/customer.model';
import { MeetingTypesModel } from '@models/meeting-types.model';
import { PartnerDbModel, TokenType } from '@models/partner-db.model';
import { VerifyPaymentStatusModel } from '@models/partner-setting.model';
import { AppointmentCartItem, CurrentSelectedAppointmentState, PaymentMethodData, SelectedBookingDataModel } from '@models/selected-booking-data.model';
import { StoreDbModel } from '@models/store-db.model';
import { UserInfo } from '@models/user.model';
import { WidgetAppointmentGroupModel } from '@models/widget-appointment-group.model';
import { ZipCodeItem, ZipCodeResponse } from '@models/zip-code.model';
import { WidgetContext } from '@models/widget-conf.model';
import { WidgetTemplateModel } from '@models/widget-template.model';
import { CustomFieldsService } from '@services/feature/custom-fields.service';
import { DateUtilService } from '@services/utils/date-util.service';
import { FeatureService } from '@services/utils/feature.service';
import { HelperService } from '@services/utils/helper.service';
import { HttpClientService } from '@services/utils/http-client.service';
import { LocalstorageService } from '@services/utils/localstorage.service';
import { LoggerService } from '@services/utils/logger.service';
import { normalizePostcode } from '@services/utils/store-postcode.util';
import { SwRouteService } from '@services/utils/sw-route.service';
import { catchError, map, Observable, of, Subject, Subscription, take } from 'rxjs';
import moment from 'moment';
import { switchMap } from 'rxjs/operators';
import { TimezoneService } from './timezone.service';
import { WidgetService } from './widget.service';

@Injectable({
  providedIn: 'root'
})
export class BookingService {

  private httpClientService = inject(HttpClientService);
  private localstorageService = inject(LocalstorageService);
  private dateUtilService = inject(DateUtilService);
  private swRouteService = inject(SwRouteService);
  private helperService = inject(HelperService);
  private featureService = inject(FeatureService);
  private widgetService = inject(WidgetService);
  private timezoneService = inject(TimezoneService);
  private customFieldsService = inject(CustomFieldsService);

  protected readonly dateFormat = DATE_FORMAT;
  protected readonly dateIsoFormat = DATE_ISO_FORMAT;

  selectedBookingData = new SelectedBookingDataModel();
  partnerData: PartnerDbModel;

  // Resolve the "redirect to external success page" config per smart-widget FIRST: when the widget
  // defines its own redirect config (the enabled flag is present in its context) ALL three values are
  // read from the widget, so they never mix sources. When the widget has no redirect config at all,
  // the resolver falls back wholesale to the partner-level (global) setting — preserving backward
  // compatibility for partners who configured the global redirect. Every public getter is feature-
  // gated so the whole feature stays behind the flag consistently across all redirect paths.
  private get redirectConfig(): { enabled?: number; url?: string; action?: string } {
    const context = this.widgetService.widgetConf?.context;
    if (context?.is_redirect_after_booking_enabled !== undefined) {
      return {
        enabled: context.is_redirect_after_booking_enabled,
        url: context.successful_booking_redirect_url,
        action: context.redirect_after_booking_action,
      };
    }
    return {
      enabled: this.partnerData?.is_redirect_after_booking_enabled,
      url: this.partnerData?.successful_booking_redirect_url,
      action: this.partnerData?.redirect_after_booking_action,
    };
  }

  get redirectAfterBookingEnabled(): number | undefined {
    if (!this.featureService.hasFeature('is_external_success_page_forwarding_enabled')) {
      return undefined;
    }
    return this.redirectConfig.enabled;
  }

  get successfulBookingRedirectUrl(): string | undefined {
    if (!this.featureService.hasFeature('is_external_success_page_forwarding_enabled')) {
      return undefined;
    }
    return this.redirectConfig.url;
  }

  get redirectAfterBookingAction(): string | undefined {
    if (!this.featureService.hasFeature('is_external_success_page_forwarding_enabled')) {
      return undefined;
    }
    return this.redirectConfig.action;
  }

  // Single chokepoint for the post-booking external redirect (mirrors the regular widget's
  // HelperService.redirectToUrl). NOTE: the browser may still block this when it is not triggered
  // by a direct user gesture (the summary-view / Stripe-return auto-redirect) — the success-view
  // button path is gesture-driven and reliable.
  redirectToSuccessPage(): void {
    const url = this.successfulBookingRedirectUrl;
    if (url) {
      this.helperService.redirectToUrl(url);
    }
  }

  // Success-page action gates — driven by the per-widget context flags (see WidgetContext).
  // Both are opt-in (absent/false = hidden).
  showAppointmentLinkOnSuccess(): boolean {
    return !!this.widgetService.widgetConf?.context?.show_appointment_link;
  }

  showIcsDownloadOnSuccess(): boolean {
    return !!this.widgetService.widgetConf?.context?.show_ics_download;
  }

  // When partner has opted into 'partner' mode, slot-fetch / booking / reservation payloads carry
  // the partner's configured timezone regardless of visitor's browser TZ.
  get effectiveDisplayTimezone(): string {
    if (this.partnerData?.widget_display_timezone_source === WIDGET_DISPLAY_TIMEZONE_SOURCE.PARTNER && this.partnerData?.timezone) {
      return this.partnerData.timezone;
    }
    return this.selectedBookingData?.timezone || this.timezoneService.localTimezone;
  }
  widgetTemplates: WidgetTemplateModel[] = [];
  // Appointments returned by the last successful book call. Lives OUTSIDE selectedBookingData
  // on purpose: resetBookingDataAfterBooking() runs BEFORE the success view mounts and clears the
  // booking-selection state — the success view still needs these to render the appointment-link /
  // ICS actions. Overwritten (cleared) at the start of every booking attempt.
  lastBookedAppointments: BookedAppointment[] = [];
  lastView: string;
  hiddenCustomFields: CustomFieldModel[] = [];

  // [KAPO / generic — hidden_booking_steps] Hidden steps that could NOT resolve a forced value
  // at runtime and must therefore render normally (safety — never an invisible dead-end).
  readonly hiddenStepResolutionFailed = new Set<string>();

  /**
   * True when the given booking view is configured to be removed from the flow.
   * Only MEETING_TYPE/STORE/SERVICES/WORKERS are hideable (they have force-skip + fallback wired
   * up); any other view is ignored even if present in the config, so an unsupported key can't opt
   * a step out of the one-page stack with no resolver.
   */
  isStepHidden(view: string): boolean {
    return HIDEABLE_BOOKING_STEPS.includes(view)
      && !!this.widgetService.widgetConf?.context?.hidden_booking_steps?.includes(view);
  }

  /** The meeting-type step is part of the flow: rendered as a picker, OR hidden with a forced provider. */
  isMeetingTypeStepInFlow(): boolean {
    return !!this.widgetService.widgetConf?.context?.show_meeting_type_picker
      || this.isStepHidden(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW);
  }

  /** A hidden step could not force its value → render it normally instead of skipping. */
  markHiddenStepUnresolved(view: string): void {
    this.hiddenStepResolutionFailed.add(view);
    LoggerService.warn(`[hidden_booking_steps] "${view}" could not resolve a forced value — rendering it normally.`);
  }

  /**
   * True when we are navigating BACKWARD into `view` (the previous view has higher precedence).
   * Generalizes the per-component `lastView === SLOTS_VIEW` back-nav checks so a hidden step
   * can skip in the correct direction through a chain of consecutive hidden steps.
   */
  isNavigatingBackTo(view: string): boolean {
    const last = SECTION_PRECEDENCE[this.lastView];
    const target = SECTION_PRECEDENCE[view];
    return last != null && target != null && last > target;
  }
  gtmStartTriggered = false;
  initialSmartWidgetView: string;
  isDefaultPage = false;
  meetingTypeList: MeetingTypesModel[] = [];
  isCombiServiceSelected = false;

  // [BKB — auto_load_booking_questions] Service-resolved booking questions (visible only; hidden
  // ones are routed to hiddenCustomFields by the same post-processing as the manual/context path).
  // Signal because Phase 2 wires it to reactive one-page-UI rendering — see docs/decisions.md ADR-019.
  readonly resolvedBookingQuestions = signal<CustomFieldModel[]>([]);

  // [auto_load_booking_questions — review fixes] Cancels a superseded in-flight fetch so a stale
  // response for a since-changed service selection can never land and overwrite a newer resolve.
  private autoLoadSubscription?: Subscription;
  // Ids (visible + hidden) our OWN auto-load mechanism last introduced into hiddenCustomFields /
  // selectedCustomFieldValues — scopes pruning to what we added, never touching legacy/context data.
  // [review-fix N-A] Also includes DECLARED dependency-child ids reachable from the resolved
  // top-level questions (child_custom_field_id chain + custom_field_options[].dependencies CSV),
  // not just the ids the endpoint returned directly — see collectDeclaredDescendantIds().
  private previouslyAutoLoadedIds = new Set<number>();
  private readonly autoLoadResolving = signal(false);
  private readonly autoLoadFetchFailed = signal(false);
  private readonly autoLoadSettled$ = new Subject<void>();

  // Fires whenever a store-select answer changes the appointment state's store context.
  // Every mounted worker-select question listens so it can re-apply the store scope to its
  // advisor list. Without it the scope is only ever applied in the worker question's ngOnInit,
  // which is inert wherever a store-select and a worker-select are mounted together — so both
  // ngOnInits run before either answer exists and nothing re-filters afterwards. That happens
  // on the personal form, in a nested dependency chain (#recursiveListTmpl co-mounts a question
  // with its dependencies, so it occurs on the stepped view too), and under one-page UI's
  // cross-step stacking, where the before-questions step and the personal form stay mounted
  // together. Only the plain stepped case — two TOP-LEVEL questions, store answered first — is
  // safe on mount order alone.
  private readonly storeContextChanged$ = new Subject<void>();

  notifyStoreContextChanged(): void {
    this.storeContextChanged$.next();
  }

  whenStoreContextChanges(): Observable<void> {
    return this.storeContextChanged$.asObservable();
  }

  /** True only when the partner explicitly opted in — absence/false is the default (AC5). */
  isAutoLoadBookingQuestionsEnabled(): boolean {
    return !!this.widgetService.widgetConf?.context?.auto_load_booking_questions;
  }

  /**
   * Centralizes the raw `context.show_questions_before_booking_process` read so every navigation
   * guard agrees on the same mode. Independent of auto_load_booking_questions: the
   * before-step asks the manually selected context questions, which exist regardless of the
   * service selection — only the SERVICE-RESOLVED questions require a selected service, and those
   * render at the auto-load placement target (see getPlacementTargetQuestionIds()).
   */
  isBeforeQuestionsMode(): boolean {
    return !!this.widgetService.widgetConf?.context?.show_questions_before_booking_process;
  }

  /**
   * Where the auto-loaded questions render when the flag is ON. Defaults to 'dedicated_step' when
   * absent/unset — the existing post-slots questions step keeps rendering exactly as before
   * (AC5 byte-for-byte). Only an explicit 'personal_form' activates the embedded-in-personal-form
   * path (see isAutoLoadInPersonalForm()).
   */
  getAutoLoadBookingQuestionsPlacement(): 'dedicated_step' | 'personal_form' {
    return this.widgetService.widgetConf?.context?.auto_load_booking_questions_placement ?? 'dedicated_step';
  }

  /**
   * Where the auto-loaded questions sit RELATIVE to the manually-configured ones. Defaults to
   * 'bottom' when absent/unset — they keep being appended exactly as before (byte-for-byte).
   * 'top' prepends them at the front of the list instead; the partner needs this because the
   * loaded set depends on the customer's service choice and therefore cannot be ordered by hand
   * in the setup page (which only lists the manually-configured questions).
   *
   * ONLY meaningful for placement 'personal_form', and deliberately forced to 'bottom' otherwise
   * so the setting can never act invisibly: the dashboard shows the dropdown only for that
   * placement, and a value left over from a previous placement choice must not silently reorder
   * the dedicated step. Two reasons this is the right scope rather than an arbitrary restriction:
   *
   * - In the dedicated step the questions are the ONLY thing on screen, so the partner already
   *   controls the order by curating context.booking_questions; the personal form is the case
   *   where the loaded questions have to be placed against fields (the contact block) that the
   *   questions list cannot express at all.
   * - With show_questions_before_booking_process ON — BKB's actual configuration — the dedicated
   *   step's placement target is getPlacementTargetQuestionIds(), i.e. the effective set MINUS
   *   every context.booking_questions id, which leaves service-resolved ids only. Reordering
   *   "context ids vs service ids" in a list that contains no context ids is a no-op, so the
   *   setting could not have done anything there anyway.
   */
  getAutoLoadBookingQuestionsPosition(): 'top' | 'bottom' {
    if (!this.isAutoLoadInPersonalForm()) {
      return 'bottom';
    }
    return this.widgetService.widgetConf?.context?.auto_load_booking_questions_position ?? 'bottom';
  }

  /** True only when auto-load is ON AND the partner chose to embed the questions in the personal
   *  form instead of the dedicated post-slots step. */
  isAutoLoadInPersonalForm(): boolean {
    return this.isAutoLoadBookingQuestionsEnabled() && this.getAutoLoadBookingQuestionsPlacement() === 'personal_form';
  }

  /**
   * The id list every AFTER-questions nav guard/render must use instead of the raw
   * `context.booking_questions` read. Flag OFF -> returns the context list VERBATIM (byte-for-byte
   * legacy path, AC3/AC5). Flag ON -> de-duplicated union of the context ids and the service-resolved
   * ids (AC4), ordered by getAutoLoadBookingQuestionsPosition() — 'bottom' (default) keeps the
   * context ids first so a manually-curated order is preserved for the shared part. That getter
   * only ever returns 'top' for placement 'personal_form', so the dedicated step keeps its legacy
   * order unconditionally.
   *
   * The position setting moves the SERVICE-ONLY block. A question that the partner also placed in
   * `context.booking_questions` keeps the slot they gave it there, under both positions — an
   * explicitly curated position must not be overridden by the same question happening to also hang
   * off the selected service.
   */
  getEffectiveBookingQuestionIds(): number[] {
    const contextIds = this.widgetService.widgetConf?.context?.booking_questions ?? [];
    if (!this.isAutoLoadBookingQuestionsEnabled()) {
      return contextIds;
    }
    const resolvedIds = this.resolvedBookingQuestions().map(question => question.id);
    const serviceOnlyIds = resolvedIds.filter(id => !contextIds.includes(id));
    return this.getAutoLoadBookingQuestionsPosition() === 'top'
      ? Array.from(new Set([...serviceOnlyIds, ...contextIds]))
      : Array.from(new Set([...contextIds, ...serviceOnlyIds]));
  }

  /**
   * The manually selected question ids asked in the BEFORE step. Empty unless
   * show_questions_before_booking_process is set — when it is, these ids are asked up front and
   * must never be asked a second time at the auto-load placement target.
   */
  getBeforeStepQuestionIds(): number[] {
    return this.isBeforeQuestionsMode()
      ? (this.widgetService.widgetConf?.context?.booking_questions ?? [])
      : [];
  }

  /**
   * The id list the auto-load placement target (dedicated AFTER step or personal-form embed)
   * renders/guards on: the effective set minus the ids already asked in the BEFORE step.
   * Before-mode OFF -> identical to getEffectiveBookingQuestionIds() (legacy/union behaviour,
   * byte-for-byte). Before-mode ON + auto-load OFF -> empty (the before-step asked everything).
   * Before-mode ON + auto-load ON -> the service-resolved questions only (the
   * qualification question stays FIRST, the service questions appear additionally, nothing twice).
   */
  getPlacementTargetQuestionIds(): number[] {
    const beforeStepIds = new Set(this.getBeforeStepQuestionIds());
    if (!beforeStepIds.size) {
      return this.getEffectiveBookingQuestionIds();
    }
    return this.getEffectiveBookingQuestionIds().filter(id => !beforeStepIds.has(id));
  }

  /**
   * Fetches the booking questions configured on the selected appointment service(s) and stores the
   * resolved (visible) set. Guarded by the flag so calling this unconditionally on every
   * select/deselect is always safe/idempotent (AC6 re-resolve) — a no-op when the flag is OFF, so it
   * introduces zero side effects on an existing smart-widget (AC3/AC5).
   *
   * Review-fix hardening (JB-1648):
   * - #4 race: cancels any still in-flight fetch first — HttpClient observables are cold and
   *   cancel the underlying request on unsubscribe, so a stale response for a since-superseded
   *   selection can never land and overwrite a newer resolve.
   * - #1/#2 stale answers: prunes any previously auto-loaded hidden entry / submitted answer that
   *   isn't part of the fresh set (and isn't a permanent context.booking_questions id) — see
   *   pruneStaleAutoLoadedAnswers().
   * - N-A dependency-child stale answers: the "owned" id set passed to pruneStaleAutoLoadedAnswers
   *   also includes DECLARED dependency-child ids reachable from the resolved questions
   *   (child_custom_field_id chain + custom_field_options[].dependencies CSV) — see
   *   collectDeclaredDescendantIds() — so a child question loaded later (when the customer answers
   *   the parent, via booking-question-view's own getCustomFieldDependencies()) is pruned too when
   *   its parent no longer resolves, closing the one-level-deeper AC6 leak.
   * - #3 error handling: a transport error does NOT clear resolvedBookingQuestions (never silently
   *   treated as "0 questions"); it flips hasAutoLoadFetchFailed() so the questions view can
   *   surface the misconfiguration error instead of auto-skipping a BKB-required question.
   * - #5/#6 pending state: isAutoLoadQuestionsResolving()/whenAutoLoadSettled() let the slots-nav
   *   guard and the questions view coordinate around an in-flight fetch instead of deciding early.
   */
  resolveAutoLoadedBookingQuestions(serviceIds: number[]): void {
    if (!this.isAutoLoadBookingQuestionsEnabled()) {
      return;
    }

    this.autoLoadSubscription?.unsubscribe();

    if (!serviceIds?.length) {
      this.resolvedBookingQuestions.set([]);
      this.pruneStaleAutoLoadedAnswers(new Set<number>());
      this.autoLoadFetchFailed.set(false);
      this.autoLoadResolving.set(false);
      this.autoLoadSettled$.next();
      return;
    }

    this.autoLoadResolving.set(true);
    this.autoLoadSubscription = this.customFieldsService.getAppointmentCustomFields(serviceIds).subscribe({
      next: (customFields: CustomFieldModel[]) => {
        // #7: resolve identifier-keyed prefill entries against the service-resolved set too (same
        // call the legacy/context branch already makes) so JB-1499 identifier prefill (URL query /
        // custom-field-<identifier> WC attribute) maps onto auto-loaded questions as well.
        this.swRouteService.resolveCustomFieldIdentifiers(customFields);

        // [review-fix N-A] Owned set = top-level resolved ids + every DECLARED dependency-child id
        // reachable from them, so a child answered later (after the customer picks a triggering
        // option/value) is still recognised as "ours" and pruned on the next resolve if stale.
        const newAutoLoadedIds = new Set<number>((customFields ?? []).map(field => field.id));
        for (const descendantId of this.collectDeclaredDescendantIds(customFields ?? [])) {
          newAutoLoadedIds.add(descendantId);
        }
        this.pruneStaleAutoLoadedAnswers(newAutoLoadedIds);

        this.resolvedBookingQuestions.set(this.processAutoLoadedBookingQuestions(customFields));
        this.autoLoadFetchFailed.set(false);
        this.autoLoadResolving.set(false);
        this.autoLoadSettled$.next();
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.error(error);
        this.autoLoadFetchFailed.set(true);
        this.autoLoadResolving.set(false);
        this.autoLoadSettled$.next();
      }
    });
  }

  /** True while a fetch triggered by resolveAutoLoadedBookingQuestions() is in flight. */
  isAutoLoadQuestionsResolving(): boolean {
    return this.autoLoadResolving();
  }

  /** True when the most recently SETTLED auto-load fetch ended in a transport error (AC-#3). */
  hasAutoLoadFetchFailed(): boolean {
    return this.autoLoadFetchFailed();
  }

  /**
   * Emits once the in-flight auto-load fetch (if any) settles; emits immediately when nothing is
   * pending. Lets a view that mounts mid-fetch (service selected right before slots-next) wait for
   * the real answer instead of deciding off a stale getEffectiveBookingQuestionIds() read.
   */
  whenAutoLoadSettled(): Observable<void> {
    if (!this.autoLoadResolving()) {
      return of(undefined);
    }
    return this.autoLoadSettled$.pipe(take(1));
  }

  // Same post-processing as booking-questions-view.getCustomFieldsByIds(): sort select/radio
  // options, and route is_hidden questions to hiddenCustomFields instead of rendering them.
  //
  // [review-fix] dependency-child parity with the classic widget: any customField whose
  // parent_custom_field_id is set is a dependency CHILD, not a standalone question — the classic
  // widget filters these out identically (appointment.component.ts prefillCustomFields():
  // `!customFields[y]['parent_custom_field_id']`). Skipped entirely here (neither visible nor
  // hidden) — it must never render as an independent top-level question, and it must never be
  // routed to hiddenCustomFields either (that would submit it unanswered). It is still correctly
  // pruned later if stale, because collectDeclaredDescendantIds() walks the parents' declared
  // child ids into the "owned by auto-load" set independently of this method.
  private processAutoLoadedBookingQuestions(customFields: CustomFieldModel[]): CustomFieldModel[] {
    if (!customFields?.length) {
      return [];
    }

    const visibleQuestions: CustomFieldModel[] = [];
    for (const customField of customFields) {
      if (customField.parent_custom_field_id) {
        continue;
      }

      if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT
        || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO
        || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD
        || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD
        || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD) {
        customField.custom_field_options = customField.alphabetically_sorted === 1
          ? this.helperService.sortByNumber(customField.custom_field_options, 'label')
          : this.helperService.sortByNumber(customField.custom_field_options, 'position');
      }

      if (customField.is_hidden === 0) {
        visibleQuestions.push(customField);
      } else {
        this.hiddenCustomFields.push(customField);
      }
    }

    // custom_fields.position is a VARCHAR, so the API's `ORDER BY position ASC` sorts it as text
    // and hands back -4,-3,-2,-1 as "-1","-2","-3","-4". Sort numerically, as the classic widget
    // already does (compact-widget.component.ts). Stable sort keeps equal positions in API order.
    return visibleQuestions.sort((a, b) => this.toSortablePosition(a) - this.toSortablePosition(b));
  }

  /** Numeric `position`. Not HelperService.sortByNumber(): its `<`/`>` on these strings is the bug. */
  private toSortablePosition(customField: CustomFieldModel): number {
    const position = Number(customField?.position);
    return Number.isFinite(position) ? position : 0;
  }

  /**
   * [review-fix N-A] Walks a set of resolved questions and collects every DECLARED dependency-child
   * custom-field id reachable from them — recursively, so an N-level chain is covered like
   * updateCustomFieldValues()'s own checkForDepth recursion. Two declaration sources, both static
   * (present on the object immediately at resolve-time, independent of whether the customer has
   * actually triggered the runtime dependency fetch yet):
   * - `child_custom_field_id` (+ the BE-embedded `child_custom_field`, from the endpoint's
   *   `contain(['ChildCustomFields', ...])` — CustomFieldsController.php:691).
   * - each `custom_field_options[].dependencies` CSV — the exact source
   *   booking-question-view.component.ts's `onRadioButtonChangeEvent()` parses when the customer
   *   selects a dependency-triggering option, before it calls
   *   `customFieldsService.getCustomFieldsByIds(cfDependencies)`.
   * Declaring the full reachable set up front (rather than only ids already loaded into
   * `customFieldDependencies` at rest) means a child the customer hasn't answered yet is still
   * correctly recognised as "belongs to this resolve" the moment it IS answered later — so a
   * subsequent prune (next service switch) can catch it as stale without needing to have observed
   * the runtime fetch. A visited-guard prevents infinite loops on a malformed/circular chain.
   */
  private collectDeclaredDescendantIds(customFields: CustomFieldModel[]): Set<number> {
    const descendantIds = new Set<number>();
    const visited = new Set<number>();

    const visit = (field: CustomFieldModel | undefined): void => {
      if (!field?.id || visited.has(field.id)) {
        return;
      }
      visited.add(field.id);

      if (field.child_custom_field_id) {
        descendantIds.add(field.child_custom_field_id);
      }
      if (field.child_custom_field) {
        descendantIds.add(field.child_custom_field.id);
        visit(field.child_custom_field);
      }

      for (const option of field.custom_field_options ?? []) {
        const dependencies = option?.dependencies;
        if (typeof dependencies === 'string' && dependencies.trim() !== '') {
          for (const rawId of dependencies.split(',')) {
            const id = Number(rawId);
            if (!Number.isNaN(id)) {
              descendantIds.add(id);
            }
          }
        }
      }

      // Already-loaded runtime dependency children (populated by
      // booking-question-view.component.ts's getCustomFieldDependencies() while the customer is
      // mid-flow) — rarely present yet at initial resolve-time, but walking them too keeps this
      // exhaustive if the objects are ever re-passed in after being answered.
      for (const dependency of field.customFieldDependencies ?? []) {
        descendantIds.add(dependency.id);
        visit(dependency);
      }
    };

    for (const field of customFields ?? []) {
      visit(field);
    }

    return descendantIds;
  }

  /**
   * Every question below `customField` — direct children plus their descendants. Answering
   * replaces the branch beneath, so the abandoned branch's answers must go with it; stopping at
   * level 1 strands the deepest answer, which is the one carrying the resolved service id.
   *
   * ⚠️ SEEDED only from the LOADED children, never from the parent's own option-`dependencies`
   * CSVs (their declared descendants are then walked, which is the point). That seed is the
   * safety invariant: at mount the prefill cascade re-fires these same handlers with
   * `customFieldDependencies` empty, and seeding from CSVs there would make a restored draft
   * purge itself. Empty-in must mean empty-out.
   */
  getDescendantCustomFieldIds(customField: CustomFieldModel): number[] {
    const children = customField?.customFieldDependencies ?? [];
    if (!children.length) {
      return [];
    }

    const ids = new Set<number>(children.map(child => child.id));
    for (const descendantId of this.collectDeclaredDescendantIds(children)) {
      ids.add(descendantId);
    }

    return [...ids];
  }

  /**
   * Records on the persisted ANSWER which question it hangs off. `CustomFieldModel` already
   * carries `parent_custom_field_id` from the backend, but that lives on the loaded QUESTION,
   * which a restored draft does not have — stamping the entry is what lets the purge prune a
   * branch whose questions were never re-fetched.
   *
   * Applied AFTER the write rather than inside each push, so it holds for every push site,
   * including ones added later.
   */
  private stampDependencyParent(
    customFieldValues: { custom_field_id: number }[],
    childCustomFieldId: number,
    parentCustomFieldId: number
  ): void {
    for (const entry of customFieldValues ?? []) {
      if (entry.custom_field_id === childCustomFieldId) {
        (entry as { parent_custom_field_id?: number }).parent_custom_field_id = parentCustomFieldId;
      }
    }
  }

  /**
   * Drops an abandoned branch's persisted answers: the seed ids plus everything transitively
   * parented by them. Seeds cover only questions still in memory, so the closure walks the
   * PERSISTED entries' own `parent_custom_field_id` links — a restored draft can be pruned
   * without its question objects.
   *
   * Same shape as pruneStaleAutoLoadedAnswers(), except this does NOT exempt
   * `context.booking_questions` ids: an abandoned branch loses its answers however the question
   * was introduced.
   *
   * `changedCustomFieldId` is the question whose answer changed — it identifies which stamps
   * mean "directly beneath the branch being abandoned".
   *
   * `rearmHiddenSteps` is forwarded to the persist below: this runs from BOTH questions steps,
   * and the AFTER one sits past STORE/SERVICES.
   */
  purgeStaleDependencyAnswers(
    staleSeedIds: number[],
    changedCustomFieldId?: number,
    rearmHiddenSteps = true
  ): void {
    if (!staleSeedIds?.length) {
      return;
    }

    const entries = this.selectedBookingData?.selectedCustomFieldValues ?? [];
    const stampOf = new Map<number, number | undefined>();
    for (const entry of entries) {
      stampOf.set(entry.custom_field_id, (entry as { parent_custom_field_id?: number }).parent_custom_field_id);
    }

    // Seed only the branch ROOTS — entries stamped to the changed question, plus unstamped ones
    // (a pre-9.2.1 draft). Everything genuinely under them is reached by the closure below via the
    // same stamps, so nothing under-purges. A seed stamped ELSEWHERE was reached through a
    // different, still-live parent (one option CSV can declare the same question under several
    // parents), so it stays — and because it never enters the set, neither does its subtree.
    const stale = new Set<number>();
    for (const id of staleSeedIds) {
      const stamp = stampOf.get(id);
      if (stamp !== undefined && stamp !== changedCustomFieldId) {
        continue;
      }
      stale.add(id);
    }

    // Transitive closure — repeat until a pass adds nothing, so depth is irrelevant and a
    // malformed/circular parent chain cannot spin (the set only ever grows, and it is bounded
    // by the number of stored entries).
    let grew = true;
    while (grew) {
      grew = false;
      for (const entry of entries) {
        const parentId = (entry as { parent_custom_field_id?: number }).parent_custom_field_id;
        if (parentId !== undefined && stale.has(parentId) && !stale.has(entry.custom_field_id)) {
          stale.add(entry.custom_field_id);
          grew = true;
        }
      }
    }

    this.hiddenCustomFields = this.hiddenCustomFields.filter(field => !stale.has(field.id));
    this.selectedBookingData.selectedCustomFieldValues = entries.filter(
      value => !stale.has(value.custom_field_id)
    );

    this.setCustomFieldValuesInLocalStorage(this.selectedBookingData.selectedCustomFieldValues, rearmHiddenSteps);
  }

  /**
   * Removes any id our OWN auto-load mechanism previously introduced (visible or hidden, top-level
   * or a DECLARED dependency descendant — see collectDeclaredDescendantIds()) that is neither part
   * of THIS resolve's fresh set nor a permanent context.booking_questions id, from
   * hiddenCustomFields and the submitted selectedCustomFieldValues. Scoped strictly to
   * previouslyAutoLoadedIds, so legacy/context-driven hidden entries and answers — and anything
   * from unrelated flows (personal-form-embedded questions, dependency children of a NON-auto-loaded
   * question) — are never touched. No-op when nothing was previously auto-loaded (e.g. first-ever
   * resolve).
   */
  private pruneStaleAutoLoadedAnswers(newAutoLoadedIds: Set<number>): void {
    const contextIds = new Set<number>(this.widgetService.widgetConf?.context?.booking_questions ?? []);
    const staleIds = new Set<number>();
    for (const id of this.previouslyAutoLoadedIds) {
      if (!newAutoLoadedIds.has(id) && !contextIds.has(id)) {
        staleIds.add(id);
      }
    }

    if (staleIds.size) {
      this.hiddenCustomFields = this.hiddenCustomFields.filter(field => !staleIds.has(field.id));
      this.selectedBookingData.selectedCustomFieldValues = this.selectedBookingData.selectedCustomFieldValues.filter(
        value => !staleIds.has(value.custom_field_id)
      );
    }

    this.previouslyAutoLoadedIds = newAutoLoadedIds;
  }

  initializeBookingWidget(partnerUUID: string, tokenType: TokenType): Observable<PartnerDbModel> {
    const body: { partner_uuid: string, token_type: TokenType } = { partner_uuid: partnerUUID, token_type: tokenType };
    return this.httpClientService.post('partners/information', body, {
      context: new HttpContext().set(IS_CALENSO_AUTH_REQUIRED, true)
    }).pipe(
      switchMap((partner: PartnerDbModel) => {
        this.featureService.loadSubscriptionFeatures(partner.features);
        return of(partner);
      })
    );
  }

  getWidgetTemplates(): Observable<WidgetTemplateModel[]> {
    return this.httpClientService.get('templates/widget', {});
  }

  getStores(params: Params = {}): Observable<StoreDbModel[]> {
    return this.httpClientService.post('stores/filter', params, {});
  }

  getStoreByIdOrUUid(storeIdOrUuid: number): Observable<StoreDbModel> {
    return this.httpClientService.get(`stores/${storeIdOrUuid}`, {});
  }

  getStoresWithWorkers(appointmentServiceId: number[] | null): Observable<StoreDbModel[]> {
    const body = { appointment_service_ids: appointmentServiceId };
    return this.httpClientService.post('stores/stores_with_workers', body, {}).pipe(
      switchMap((stores: StoreDbModel[]) => {
        return of(stores?.length ? this.helperService.sortByNumber(stores, 'position') : []);
      })
    );
  }

  getStoresByPostCode(postcode: string): Observable<StoreDbModel[]> {
    return this.httpClientService.post('stores/store_by_postcode', { postcode }, {}).pipe(
      switchMap(stores => {
        if (stores?.length) {
          const finalStores = stores.map(store => {
            if (store?.store) {
              return {
                postcode_details: {
                  id: store.id,
                  city: store.city,
                  postcode: store.postcode
                },
                postcode_store_id: `${store.store.id}-${store.id}`,
                ...store.store
              }
            } else {
              return {
                postcode_store_id: `${store.id}`,
                ...store
              }
            }
          });

          if (finalStores?.length) {
            // Backend returns rows ordered by distance when geocoding succeeded.
            // Preserve that order; otherwise fall back to position sort to
            // stay aligned with the widget's behaviour.
            if (finalStores.some((s: any) => s?.distance != null)) {
              return of(finalStores);
            }
            return of(this.helperService.sortByNumber(finalStores, 'position'));
          }
        }

        return of([]);
      })
    );
  }

  getAppointmentSevicesByMeetingType(meetingTypeId: number, storeId?: number): Observable<AppointmentServiceModel[]> {
    const body = {
      meeting_type_id: meetingTypeId,
      ...storeId ? { store_id: storeId } : {},
      // Round Robin V2 channel scope — always sent; the BE only honours it when the RR-V2
      // feature + setting are enabled (it fails closed otherwise).
      channel: 'smart_widget'
    };
    return this.httpClientService.post(`appointment_services/appointment_services_by_meeting_type`, body, {});
  }

  getAppointmentSevices(body: { store_id: number; worker_id: number; worker_email?: string; }): Observable<AppointmentServiceModel[]> {
    return this.httpClientService.post(
      `appointment_services/appointment_services_by_store`,
      {
        ...body,
        // Round Robin V2 channel scope — always sent; the BE only honours it when the RR-V2
        // feature + setting are enabled (it fails closed otherwise).
        channel: 'smart_widget',
        ...this.getPostcodeRequestParam()
      },
      {}
    );
  }

  getWidgetAppointmentGroups(): Observable<WidgetAppointmentGroupModel[]> {
    return this.httpClientService.get(`widget_appointment_groups`, {}).pipe(
      map((groups: WidgetAppointmentGroupModel[]) => this.normalizeWidgetGroupTimes(groups))
    );
  }

  // The API serializes the groups' start/end time columns as bare "HH:mm:ss"
  // strings (full ISO datetimes before CakePHP 4); prefix a dummy date so
  // consumers can keep treating them as datetimes.
  private normalizeWidgetGroupTimes(groups: WidgetAppointmentGroupModel[]): WidgetAppointmentGroupModel[] {
    if (!Array.isArray(groups)) {
      return groups;
    }

    const toDateTime = (value: Date | string): Date | string =>
      typeof value === 'string' && !value.includes('T') ? `2000-01-01T${value}` : value;

    return groups.map(group => ({
      ...group,
      start: toDateTime(group.start),
      end: toDateTime(group.end)
    }));
  }

  getDaysWiseFreeSlots(workerId: number, storeId: number, appointmentIds: string, startDate: string, timezone: string, meetingTypeId?: number): Observable<DayWiseFreeSlotsDao> {
    const body = {
      worker_id: workerId,
      appointment_ids: appointmentIds,
      store_id: storeId,
      start: startDate,
      // Round Robin V2 channel scope — always sent; the BE only honours it when the RR-V2
      // feature + setting are enabled (it fails closed otherwise).
      channel: 'smart_widget',
      ...timezone && { timezone },
      ...meetingTypeId && { meeting_type_id: meetingTypeId },
      // Narrows "no preference" to the postcode mapping; the BE reads it only with
      // enable_store_postcode_feature.
      ...this.getPostcodeRequestParam()
    };

    return this.httpClientService.post('availabilities/free_appointments', body, {})
  }

  reserveAppointments(appointments: AppointmentReservationBody[]): Observable<{ success: boolean; }> {
    return this.httpClientService.post(`appointment_reservations`, appointments, {});
  }

  removeReservedAppointment(appointment: AppointmentReservationBody[]): Observable<{ success: boolean; }> {
    return this.httpClientService.delete(`appointment_reservations`, { body: appointment });
  }

  /**
   * Without a context: which providers the partner can use at all. With one: whether they can take
   * the money for THAT booking - e.g. whether its store has an Adyen account.
   */
  verifyOnlinePaymentStatus(context?: AdyenBookingContext): Observable<VerifyPaymentStatusModel> {
    return this.httpClientService.get('partners/verify_online_payment_status', context ? { params: { ...context } } : {});
  }

  applyCoupon(couponPayload: { coupon: string, email?: string, amount?: number }): Observable<CouponDbModel> {
    return this.httpClientService.post(`coupons/verify`, couponPayload, {});
  }

  bookAppointments(bookingItems: AppointmentBookingModel[]): Observable<{ success: boolean, errors: { message: string }[], appointments?: BookedAppointment[] }> {
    return this.httpClientService.post('appointments/book', bookingItems, {});
  }

  downloadAppointmentIcs(uuid: string): Observable<{ file: string, type: string }> {
    return this.httpClientService.get(`appointments/download/${uuid}`, {});
  }

  setCustomFieldValuesInLocalStorage(
    customFieldValues: { custom_field_id: number; type: string; value?: any; file_ids?: any }[],
    rearmHiddenSteps = true
  ): void {
    // [hidden_booking_steps] The answered set is the INPUT to hidden-step resolution, so a
    // qualification change invalidates a previous "could not resolve" verdict — without this the
    // latch is one-way and an un-hidden step stays visible for the session. Callers writing from
    // AFTER those steps pass false: nothing remounts to re-derive the verdict there, so re-arming
    // would re-hide a step the customer had already completed by hand.
    if (rearmHiddenSteps) {
      this.hiddenStepResolutionFailed.clear();
    }

    this.localstorageService.setItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_CUSTOM_FIELD + this.partnerData.booking_name,
      JSON.stringify(customFieldValues)
    );
  }

  setAppointmentStateInLocalStorage(currentSelectedAppointmentState: CurrentSelectedAppointmentState): void {
    this.localstorageService.setItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_APPOINTMENT_STATE + this.partnerData.booking_name,
      JSON.stringify(currentSelectedAppointmentState)
    );
  }

  setAppointmentCartItemsInLocalStorage(cartItems: AppointmentCartItem[]): void {
    this.localstorageService.setItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_APPOINTMENT_CART_ITEMS + this.partnerData.booking_name,
      JSON.stringify(cartItems)
    );
  }

  setAppointmentSelectedUserInLocalStorage(selectedUser: UserInfo): void {
    this.localstorageService.setItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_APPOINTMENT_SELECTED_USER + this.partnerData.booking_name,
      JSON.stringify(selectedUser)
    );
  }

  loadAppointmentStateFromLocalStorage(): void {
    const value: string = this.localstorageService.getItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_APPOINTMENT_STATE + this.partnerData.booking_name
    );
    if (value) {
      this.selectedBookingData.currentSelectedAppointmentState = JSON.parse(value);
    }
  }

  loadAppointmentCartItemsFromLocalStorage(): void {
    const value: string = this.localstorageService.getItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_APPOINTMENT_CART_ITEMS + this.partnerData.booking_name
    );
    if (value) {
      this.selectedBookingData.cartItems = JSON.parse(value);
    }
  }

  /** Returns whether a saved customer was found (and loaded). */
  loadAppointmentSelectedUserFromLocalStorage(): boolean {
    const value: string = this.localstorageService.getItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_APPOINTMENT_SELECTED_USER + this.partnerData.booking_name
    );
    if (value) {
      this.selectedBookingData.selectedUser = JSON.parse(value);
    }
    return !!value;
  }

  setupCustomFieldSelectedValues(
    widgetQuestion: CustomFieldModel,
    customFieldValues: { custom_field_id: number, type: string, value?: any, file_ids?: any }[]
  ): {
    custom_field_id: number,
    type: string,
    value?: any,
    file_ids?: any,
    supported_appointment_service_ids?: number[],
    supported_meeting_type_ids?: number[]
  }[] {
    customFieldValues = customFieldValues.filter((item: { custom_field_id: number, type: string, value?: any, file_ids?: any }) => {
      return item.custom_field_id !== widgetQuestion.id;
    });
    this.updateCustomFieldValues(widgetQuestion, customFieldValues);

    if (widgetQuestion.customFieldDependencies?.length > 0) {
      for (const dependendantWidgetQuestion of widgetQuestion.customFieldDependencies) {
        customFieldValues = customFieldValues.filter((item: { custom_field_id: number, type: string, value?: any, file_ids?: any }) => {
          return item.custom_field_id !== dependendantWidgetQuestion.id;
        });
        this.updateCustomFieldValues(dependendantWidgetQuestion, customFieldValues, true);
        this.stampDependencyParent(customFieldValues, dependendantWidgetQuestion.id, widgetQuestion.id);
      }
    }
    return customFieldValues;
  }

  handleHiddenCustomFields(
    finalCustomFieldValues: {
      custom_field_id: number,
      type: string,
      value?: any, file_ids?: any
    }[],
    hiddenCustomFields: CustomFieldModel[]
  ): {
    custom_field_id: number,
    type: string,
    value?: any, file_ids?: any
  }[] {
    if (hiddenCustomFields?.length > 0) {
      const selectTypes = [
        CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT,
        CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD,
        CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD,
        CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD,
        CUSTOM_FIELD_TYPES.CUSTOM_FIELD_WORKER_SELECT,
        CUSTOM_FIELD_TYPES.CUSTOM_FIELD_STORE_SELECT,
      ];

      hiddenCustomFields = hiddenCustomFields.filter((v: CustomFieldModel, i: number, a: CustomFieldModel[]) => a.findIndex(t => (t.id === v.id)) === i);
      const queryParamCustomFieldValues = this.swRouteService.getCustomFieldValues();

      for (const hiddenCustomField of hiddenCustomFields) {
        if (hiddenCustomField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE) {
          continue;
        }

        if (queryParamCustomFieldValues?.length) {
          const foundedQueryParamCustomField = queryParamCustomFieldValues.find(item => item.id === hiddenCustomField.id);
          if (!foundedQueryParamCustomField?.ignore && foundedQueryParamCustomField?.id) {
            this.prefillCustomFieldValueFromQueryParams(hiddenCustomField, foundedQueryParamCustomField.value);
          }
        }

        finalCustomFieldValues = finalCustomFieldValues.filter(item => item.custom_field_id !== hiddenCustomField.id);

        if (hiddenCustomField.customerSelectedValue !== undefined && hiddenCustomField.customerSelectedValue !== null) {
          this.updateCustomFieldValues(hiddenCustomField, finalCustomFieldValues);
        } else if (selectTypes.includes(hiddenCustomField.type)) {
          finalCustomFieldValues.push({
            custom_field_id: Number(hiddenCustomField.id),
            type: hiddenCustomField.type,
            value: []
          });
        } else {
          finalCustomFieldValues.push({
            custom_field_id: Number(hiddenCustomField.id),
            type: hiddenCustomField.type,
            value: ''
          });
        }
      }
    }

    return finalCustomFieldValues;
  }

  prefillCustomFieldValueFromQueryParams(customField: CustomFieldModel, value: any): void {
    if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT) {
      if (customField.is_multiple_select === 1) {
        if (Array.isArray(value)) {
          customField.customerSelectedValue = value.map(id => Number(id));
        } else {
          customField.customerSelectedValue = [Number(value)];
        }
      } else {
        customField.customerSelectedValue = (value) ? Number(value) : undefined;
      }
    } else if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD || customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD) {
      if (customField.is_multiple_select === 1) {
        if (Array.isArray(value)) {
          customField.customerSelectedValue = value.map(id => Number(id));
        } else {
          customField.customerSelectedValue = [Number(value)];
        }
      } else {
        customField.customerSelectedValue = (value) ? [Number(value)] : [];
      }
    } else if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE) {
      // TODO currently file custom field is not supporting query params
    } else if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO) {
      customField.customerSelectedValue = Number(value);
    } else if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_DATE) {
      customField.customerSelectedValue = value;
    } else if (customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_CHECKBOX) {
      customField.customerSelectedValue = (value === true || value === 'true') ? true : false;
    } else {
      customField.customerSelectedValue = value;
    }
  }

  setupFinalCustomFields(
    widgetQuestions: CustomFieldModel[],
    customFieldValues: {
      custom_field_id: number,
      type: string,
      value?: any, file_ids?: any
    }[] = []
  ): {
    custom_field_id: number, type: string, value?: any, file_ids?: any, option_values?: {
      custom_field_option_id: number,
      custom_field_option_value: string
    }[]
  }[] {
    for (const widgetQuestion of widgetQuestions) {
      customFieldValues = customFieldValues.filter(item => item.custom_field_id !== widgetQuestion.id);
      this.updateCustomFieldValues(widgetQuestion, customFieldValues);
      if (widgetQuestion.customFieldDependencies?.length) {
        for (const dependendantWidgetQuestion of widgetQuestion.customFieldDependencies) {
          this.updateCustomFieldValues(dependendantWidgetQuestion, customFieldValues);
          this.stampDependencyParent(customFieldValues, dependendantWidgetQuestion.id, widgetQuestion.id);
        }
      }
    }

    return customFieldValues;
  }

  updateCustomFieldValues(
    widgetQuestion: CustomFieldModel,
    customFieldValues: {
      custom_field_id: number,
      type: string,
      value?: any,
      file_ids?: any,
      option_values?: {
        custom_field_option_id: number,
        custom_field_option_value: string
      }[],
      supported_appointment_service_ids?: number[],
      supported_meeting_type_ids?: number[]
    }[],
    checkForDepth = false
  ): void {
    if (!widgetQuestion.customerSelectedValue) {
      LoggerService.warn('Value is not selected for custom field ', widgetQuestion);
      return;
    }

    if (widgetQuestion.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT) {
      if (Array.isArray(widgetQuestion.customerSelectedValue)) {
        const finalValues = [];
        for (const value of widgetQuestion.customerSelectedValue) {
          if (Array.isArray(value)) {
            finalValues.push(...value);
          } else {
            finalValues.push(value);
          }
        }

        customFieldValues.push({
          custom_field_id: Number(widgetQuestion.id),
          type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT,
          value: finalValues,
          supported_appointment_service_ids: this.getSupportedAppointmentServiceIds(widgetQuestion),
          supported_meeting_type_ids: this.getSupportedMeetingTypeIds(widgetQuestion)
        });
      } else {
        if (widgetQuestion.customerSelectedValue) {
          customFieldValues.push({
            custom_field_id: Number(widgetQuestion.id),
            type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT,
            value: [widgetQuestion.customerSelectedValue],
            supported_appointment_service_ids: this.getSupportedAppointmentServiceIds(widgetQuestion),
            supported_meeting_type_ids: this.getSupportedMeetingTypeIds(widgetQuestion)
          });
        }
      }
    } else if (widgetQuestion.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE) {
      const finalValues = [];
      for (const value of widgetQuestion.customerSelectedValue) {
        if (Array.isArray(value)) {
          finalValues.push(...value);
        } else {
          finalValues.push(value);
        }
      }

      customFieldValues.push({
        custom_field_id: Number(widgetQuestion.id),
        type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE,
        file_ids: Object.assign([], finalValues)
      });
    } else if (widgetQuestion.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO) {
      customFieldValues.push({
        custom_field_id: Number(widgetQuestion.id),
        type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO,
        value: widgetQuestion.customerSelectedValue.toString(),
        supported_appointment_service_ids: this.getSupportedAppointmentServiceIds(widgetQuestion),
        supported_meeting_type_ids: this.getSupportedMeetingTypeIds(widgetQuestion)
      });
    } else if (widgetQuestion.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD) {
      const finalValues = [];
      for (const value of widgetQuestion.customerSelectedValue) {
        if (Array.isArray(value)) {
          finalValues.push(...value);
        } else {
          finalValues.push(value);
        }
      }

      customFieldValues.push({
        custom_field_id: Number(widgetQuestion.id),
        type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD,
        value: finalValues,
        supported_appointment_service_ids: this.getSupportedAppointmentServiceIds(widgetQuestion),
        supported_meeting_type_ids: this.getSupportedMeetingTypeIds(widgetQuestion)
      });
    } else if (widgetQuestion.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD) {
      const finalValues = [];
      for (const value of widgetQuestion.customerSelectedValue) {
        if (Array.isArray(value)) {
          finalValues.push(...value);
        } else {
          finalValues.push(value);
        }
      }

      customFieldValues.push({
        custom_field_id: Number(widgetQuestion.id),
        type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD,
        value: finalValues,
        supported_appointment_service_ids: this.getSupportedAppointmentServiceIds(widgetQuestion),
        supported_meeting_type_ids: this.getSupportedMeetingTypeIds(widgetQuestion)
      });
    } else if (widgetQuestion.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD) {
      customFieldValues.push({
        custom_field_id: Number(widgetQuestion.id),
        value: widgetQuestion.customerSelectedValue[0].map(item => item.custom_field_option_id),
        option_values: widgetQuestion.customerSelectedValue,
        type: widgetQuestion.type,
        supported_appointment_service_ids: this.getSupportedAppointmentServiceIds(widgetQuestion),
        supported_meeting_type_ids: this.getSupportedMeetingTypeIds(widgetQuestion)
      });
    } else if (widgetQuestion.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_WORKER_SELECT) {
      if (widgetQuestion.customerSelectedValue) {
        let finalValues = [];
        if (Array.isArray(widgetQuestion.customerSelectedValue)) {
          for (const value of widgetQuestion.customerSelectedValue) {
            finalValues.push(value);
          }
        } else {
          finalValues = [widgetQuestion.customerSelectedValue];
        }

        customFieldValues.push({
          custom_field_id: Number(widgetQuestion.id),
          type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_WORKER_SELECT,
          value: finalValues,
          supported_appointment_service_ids: this.getSupportedAppointmentServiceIds(widgetQuestion),
          supported_meeting_type_ids: this.getSupportedMeetingTypeIds(widgetQuestion)
        });
      }
    } else if (widgetQuestion.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_STORE_SELECT) {
      if (widgetQuestion.customerSelectedValue) {
        let finalValues = [];
        if (Array.isArray(widgetQuestion.customerSelectedValue)) {
          for (const value of widgetQuestion.customerSelectedValue) {
            finalValues.push(value);
          }
        } else {
          finalValues = [widgetQuestion.customerSelectedValue];
        }

        customFieldValues.push({
          custom_field_id: Number(widgetQuestion.id),
          type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_STORE_SELECT,
          value: finalValues,
          supported_appointment_service_ids: this.getSupportedAppointmentServiceIds(widgetQuestion),
          supported_meeting_type_ids: this.getSupportedMeetingTypeIds(widgetQuestion)
        });
      }
    } else {
      let finalValues: any;
      if (Array.isArray(widgetQuestion.customerSelectedValue)) {
        finalValues = widgetQuestion.customerSelectedValue[0];
      } else {
        finalValues = widgetQuestion.customerSelectedValue.toString();
      }

      customFieldValues.push({
        custom_field_id: Number(widgetQuestion.id),
        type: widgetQuestion.type,
        value: finalValues
      });
    }

    // Added code to support N level of dependent questions
    if (checkForDepth) {
      if (widgetQuestion?.customFieldDependencies?.length > 0) {
        for (const dependendantWidgetQuestion of widgetQuestion.customFieldDependencies) {
          this.updateCustomFieldValues(dependendantWidgetQuestion, customFieldValues, true);
          this.stampDependencyParent(customFieldValues, dependendantWidgetQuestion.id, widgetQuestion.id);
        }
      }
    }
  }

  private getSupportedIds(
    widgetQuestion: CustomFieldModel,
    optionProperty: 'supported_appointment_service_ids' | 'supported_meeting_type_ids'
  ): number[] {
    const supportedIds: number[] = [];
    if (widgetQuestion?.custom_field_options) {
      const options = (
        widgetQuestion.custom_field_options &&
        Array.isArray(widgetQuestion.custom_field_options) &&
        widgetQuestion.custom_field_options[0] != undefined &&
        Array.isArray(widgetQuestion.custom_field_options[0])
      ) ? widgetQuestion.custom_field_options[0] : widgetQuestion.custom_field_options;

      options.filter((field) => {
        if (Array.isArray(widgetQuestion.customerSelectedValue)) {
          if (Array.isArray(widgetQuestion.customerSelectedValue[0])) {
            return widgetQuestion.customerSelectedValue[0].includes(field.id);
          } else {
            return widgetQuestion.customerSelectedValue.includes(field.id);
          }
        } else {
          return Number(widgetQuestion.customerSelectedValue) === field.id;
        }
      })
        .map(field => {
          const raw = (field as any)[optionProperty];
          if (raw) {
            const ids = JSON.parse(raw);
            supportedIds.push(...ids);
          }
        });
    }
    return supportedIds;
  }

  getSupportedAppointmentServiceIds(widgetQuestion: CustomFieldModel): number[] {
    return this.getSupportedIds(widgetQuestion, 'supported_appointment_service_ids');
  }

  getSupportedMeetingTypeIds(widgetQuestion: CustomFieldModel): number[] {
    return this.getSupportedIds(widgetQuestion, 'supported_meeting_type_ids');
  }

  getMeetingTypesByIds(ids: number[]): Observable<MeetingTypesModel[]> {
    this.meetingTypeList = [];
    return this.httpClientService.post('meeting_types/get_by_ids', { ids }, {}).pipe(
      switchMap((meetingTypes: MeetingTypesModel[]) => {
        if (meetingTypes?.length) {
          meetingTypes?.forEach(meetingType => meetingType.is_multi_language = 1);
          this.meetingTypeList = meetingTypes;
          return of(meetingTypes);
        } else {
          return of([]);
        }
      })
    );
  }

  resetCurrentSelectedAppointmentStateonStoreSelection(): CurrentSelectedAppointmentState {
    const currentSelectedAppointmentState = new CurrentSelectedAppointmentState();

    if (this.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId) {
      currentSelectedAppointmentState.selectedMeetingTypeId = this.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId;
    }
    if (this.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreLeadGeneratorPostcode) {
      currentSelectedAppointmentState.selectedStoreLeadGeneratorPostcode =
        this.selectedBookingData.currentSelectedAppointmentState.selectedStoreLeadGeneratorPostcode;
      // Travels WITH the value, so a carried store zip never inherits a stale customer flag.
      currentSelectedAppointmentState.storeLeadGeneratorPostcodeEnteredByCustomer =
        this.selectedBookingData.currentSelectedAppointmentState.storeLeadGeneratorPostcodeEnteredByCustomer === true;
    }
    // Which store postcode the personal form wrote into the zip: carried unconditionally, so a later
    // store change can still replace (or remove) exactly the value the widget filled.
    currentSelectedAppointmentState.personalFormZipFilledFromPostcode =
      this.selectedBookingData?.currentSelectedAppointmentState?.personalFormZipFilledFromPostcode ?? null;
    if (this.selectedBookingData?.currentSelectedAppointmentState?.selectedServiceIds?.length > 0) {
      currentSelectedAppointmentState.selectedServiceIds = this.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds;
    }

    if (
      this.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId ||
      this.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId === 0
    ) {
      currentSelectedAppointmentState.selectedWorkerId = this.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId;
    }

    if (this.selectedBookingData?.currentSelectedAppointmentState?.selectedWorker) {
      currentSelectedAppointmentState.selectedWorker = this.selectedBookingData.currentSelectedAppointmentState.selectedWorker;
    }

    if (this.selectedBookingData?.currentSelectedAppointmentState?.selectedDate) {
      currentSelectedAppointmentState.selectedDate = this.selectedBookingData.currentSelectedAppointmentState.selectedDate;
    }

    if (this.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreId) {
      currentSelectedAppointmentState.selectedStoreId = this.selectedBookingData.currentSelectedAppointmentState.selectedStoreId;
      currentSelectedAppointmentState.selectedStore = this.selectedBookingData.currentSelectedAppointmentState.selectedStore;
    }

    if (this.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreUuids) {
      currentSelectedAppointmentState.selectedStoreUuids = this.selectedBookingData.currentSelectedAppointmentState.selectedStoreUuids;
    }

    if (this.selectedBookingData?.currentSelectedAppointmentState?.storeAndWorkerPreSelectedThroughWorkerSelectField) {
      currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField = this.selectedBookingData.currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField;
      // Travels WITH the flag. Carrying one without the other leaves the flag true and the id
      // undefined, and the option restriction then silently stops matching its own question.
      currentSelectedAppointmentState.workerSelectQuestionId = this.selectedBookingData.currentSelectedAppointmentState.workerSelectQuestionId;
    }

    if (this.selectedBookingData?.currentSelectedAppointmentState?.storePreSelectedThroughStoreSelectField) {
      currentSelectedAppointmentState.storePreSelectedThroughStoreSelectField = this.selectedBookingData.currentSelectedAppointmentState.storePreSelectedThroughStoreSelectField;
    }

    if (this.selectedBookingData?.currentSelectedAppointmentState?.storeDefaultWorkerIds) {
      currentSelectedAppointmentState.storeDefaultWorkerIds = this.selectedBookingData.currentSelectedAppointmentState.storeDefaultWorkerIds;
    }

    return currentSelectedAppointmentState;
  }

  /**
   * The postcode the customer entered in the store search, or null. This is the only value that may
   * be prefilled into the personal form or sent as the `postcode` request param: a store's own `zip`,
   * written into the same state field by a booking question, never qualifies. Strict `=== true` so a
   * persisted state from before the flag existed reads as "not entered by the customer".
   */
  getCustomerEnteredStorePostcode(): string | null {
    const state = this.selectedBookingData?.currentSelectedAppointmentState;
    if (state?.storeLeadGeneratorPostcodeEnteredByCustomer === true && state.selectedStoreLeadGeneratorPostcode) {
      return state.selectedStoreLeadGeneratorPostcode;
    }
    return null;
  }

  /**
   * `postcode` for the endpoints whose backend narrows "no preference" and the service's resources
   * to the partner's postcode mapping (availabilities/free_appointments,
   * appointment_services/appointment_services_by_store). Sent only when the partner has
   * enable_store_postcode_feature on - the backend reads the param only then - and only the
   * postcode the customer entered in the store search, never a store's own zip.
   */
  private getPostcodeRequestParam(): { postcode?: string } {
    if (Number(this.widgetService?.widgetConf?.partner?.enable_store_postcode_feature) !== 1) {
      return {};
    }
    const postcode = this.getCustomerEnteredStorePostcode();
    return postcode ? { postcode } : {};
  }

  preInitializeWidgetContextValues(
    context: WidgetContext,
    customerConfData: CustomerDbModel
  ): void {
    if (context?.show_meeting_type_picker && customerConfData?.meeting_type_id) {
      this.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId = customerConfData?.meeting_type_id;
    }

    if (context?.store_id) {
      this.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = context.store_id;
    }
    if (context?.store) {
      this.selectedBookingData.currentSelectedAppointmentState.selectedStore = context.store;
    }

    if (context?.store_uuids?.length) {
      this.selectedBookingData.currentSelectedAppointmentState.selectedStoreUuids = context.store_uuids;
    }

    if (context?.appointment_service_ids) {
      this.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds = context.appointment_service_ids;
    }

    if (context?.worker_id || context?.worker_id === 0) {
      this.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = context.worker_id;
    }

    if (context?.date) {
      this.getDateByPlaceholder();
    }

    if (context?.workers_view) {
      this.selectedBookingData.currentSelectedAppointmentState.workers_view = context.workers_view;
    }
  }

  getDateByPlaceholder(): void {
    const timezone = this.selectedBookingData?.timezone || this.timezoneService.localTimezone || 'Europe/Zurich';
    const placeholder = this.widgetService.widgetConf?.context?.date;
    let date: Date;
    switch (placeholder) {
      case '[TODAY]':
        date = new Date(moment.tz(moment(), timezone).format(DATE_TIME_ISO_T_FORMAT));
        break;
      case '[TOMORROW]':
        date = new Date(moment.tz(moment(), timezone).add(1, 'd').format(DATE_TIME_ISO_T_FORMAT));
        break;
      default:
        date = moment.tz(placeholder, timezone).toDate();
        break;
    }
    date.setHours(0, 0, 0, 0);
    this.selectedBookingData.currentSelectedAppointmentState.selectedDate = date;
  }


  preInitializeWidgetUserData(customerConfData: CustomerDbModel): void {
    if (!this.selectedBookingData.selectedUser) {
      this.selectedBookingData.selectedUser = new UserInfo();
    }
    this.selectedBookingData.selectedUser.eMail = customerConfData.email ? customerConfData.email : undefined;
    this.selectedBookingData.selectedUser.forename = customerConfData.prename ? customerConfData.prename : undefined;
    this.selectedBookingData.selectedUser.lastName = customerConfData.lastname ? customerConfData.lastname : undefined;
    this.selectedBookingData.selectedUser.mobile = customerConfData.mobile ? customerConfData.mobile : undefined;
    this.selectedBookingData.selectedUser.phone = customerConfData.phone ? customerConfData.phone : undefined;
    this.selectedBookingData.selectedUser.street = customerConfData.street ? customerConfData.street : undefined;
    this.selectedBookingData.selectedUser.zip = customerConfData.zip ? customerConfData.zip : undefined;
    this.selectedBookingData.selectedUser.city = customerConfData.city ? customerConfData.city : undefined;
    this.selectedBookingData.selectedUser.comment = customerConfData.comment ? customerConfData.comment : undefined;
    this.selectedBookingData.selectedUser.gender = customerConfData.salutation ? customerConfData.salutation : undefined;
    this.selectedBookingData.selectedUser.birthday = customerConfData.birthday ? this.dateUtilService.getDateFromString(customerConfData.birthday, this.dateFormat) : undefined;
    this.selectedBookingData.selectedUser.customer_notification_preference = customerConfData.notification_preference ? customerConfData.notification_preference : undefined;
    this.selectedBookingData.selectedUser.meeting_type_id = customerConfData.meeting_type_id ? customerConfData.meeting_type_id : undefined;
    this.selectedBookingData.selectedUser.company = customerConfData?.company ? customerConfData.company : undefined;
    this.selectedBookingData.selectedUser.title = customerConfData?.title ? customerConfData.title : undefined;
    this.selectedBookingData.selectedUser.country = customerConfData?.country ? customerConfData.country : undefined;
    this.selectedBookingData.selectedUser.policy = customerConfData?.policy ? customerConfData.policy : undefined;
  }

  resetBookingDataAfterBooking(): void {
    const oldStateValues = this.selectedBookingData.currentSelectedAppointmentState;

    this.gtmStartTriggered = false;
    // The zip the personal form filled from the store search belongs to the booking just made:
    // "Book Another" starts without it. A zip the customer typed stays.
    const zipFilledFromPostcode = oldStateValues?.personalFormZipFilledFromPostcode;
    const selectedUser = this.selectedBookingData.selectedUser;
    if (zipFilledFromPostcode && selectedUser && normalizePostcode(selectedUser.zip) === zipFilledFromPostcode) {
      selectedUser.zip = undefined;
      this.setAppointmentSelectedUserInLocalStorage(selectedUser);
    }
    this.localstorageService.removeItem(BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_APPOINTMENT_STATE + this.partnerData.booking_name);
    this.localstorageService.removeItem(BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_APPOINTMENT_CART_ITEMS + this.partnerData.booking_name);
    this.selectedBookingData.selectedServices = [];
    this.selectedBookingData.cartItems = [];
    this.selectedBookingData.totalBookingAmount = undefined;
    this.selectedBookingData.subTotal = undefined;
    this.selectedBookingData.totalDiscount = undefined;
    this.selectedBookingData.paymentMethodData = new PaymentMethodData();
    this.selectedBookingData.selectedCoupon = undefined;

    this.selectedBookingData.currentSelectedAppointmentState = new CurrentSelectedAppointmentState();

    // Reset isCombiServiceSelected flag when services are cleared
    this.isCombiServiceSelected = false;

    // [hidden_booking_steps] Clear per-session resolution-failure latches so a "Book Another" run
    // re-attempts the hidden-step force-skip instead of inheriting a stale un-hidden step.
    this.hiddenStepResolutionFailed.clear();

    // [auto_load_booking_questions] "Book Another": drop the resolved set and prune any
    // auto-loaded-only hidden/answer entries so the next booking cycle starts clean instead of
    // inheriting the previous service's questions. No-op when the flag is OFF (AC3/AC5) — legacy
    // hiddenCustomFields/selectedCustomFieldValues are left exactly as before this change.
    if (this.isAutoLoadBookingQuestionsEnabled()) {
      this.autoLoadSubscription?.unsubscribe();
      this.resolvedBookingQuestions.set([]);
      this.pruneStaleAutoLoadedAnswers(new Set<number>());
      this.autoLoadFetchFailed.set(false);
      this.autoLoadResolving.set(false);
    }

    // When store uuids are present preserving store and store uuids in order to resume flow on tap of rebook appointment(This has been updated for AMAG)
    if (this.selectedBookingData.currentSelectedAppointmentState?.selectedStoreUuids) {
      this.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = oldStateValues.selectedStoreId;
      this.selectedBookingData.currentSelectedAppointmentState.selectedStore = oldStateValues.selectedStore;
      this.selectedBookingData.currentSelectedAppointmentState.selectedStoreUuids = oldStateValues.selectedStoreUuids;
    }
  }

  getSupportedAppointmentServiceIdsFromLocalStorage(bookingName: string): number[] {
    return this.getSupportedIdsFromLocalStorage(bookingName, 'supported_appointment_service_ids');
  }

  getSupportedMeetingTypeIdsFromLocalStorage(bookingName: string): number[] {
    return this.getSupportedIdsFromLocalStorage(bookingName, 'supported_meeting_type_ids');
  }

  private getSupportedIdsFromLocalStorage(
    bookingName: string,
    property: 'supported_appointment_service_ids' | 'supported_meeting_type_ids'
  ): number[] {
    let customFields: any = this.localstorageService.getItem(
      BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_CUSTOM_FIELD + bookingName
    );
    if (customFields) {
      customFields = JSON.parse(customFields);
      const ids = new Set<number>();
      customFields.forEach((field: any) => {
        const value = field?.[property];
        if (value) {
          // De-duplicated: a concern and its leaf legitimately resolve the same service, which
          // otherwise yields [45702, 45702]. Every consumer is an inclusion filter or a request
          // body, so collapsing is behaviour-preserving.
          for (const id of value) {
            ids.add(id);
          }
        }
      });
      return [...ids];
    }
    return [];
  }

  getCityByZip(zip: string): Observable<CityByZipModel> {
    const params = new HttpParams().set('zip', zip);
    return this.httpClientService.get(`zip_codes`, { params });
  }

  getCitiesByZip(zip: string, limit = 15, offset = 0): Observable<ZipCodeItem[]> {
    const params = new HttpParams()
      .set('zip', zip)
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    return this.httpClientService.get(`zip_codes`, { params }).pipe(
      map((response: ZipCodeResponse) => response?.zip_code?.zips || []),
      catchError(() => of([]))
    );
  }
}
